// Deep-set/modify nested object properties whether or not they already exist
export function set(obj, path, value, operation) {
  let schema = obj;
  let paths = path.split(".");
  let len = paths.length;
  let lastPath = paths[len - 1];
  for (let i = 0; i < len - 1; i++) {
    let elem = paths[i];
    if (!schema[elem]) {
      schema[elem] = {};
    }
    schema = schema[elem];
  }
  if (operation == "push") {
    // push the new value to an array
    if (!schema[lastPath]) {
      schema[lastPath] = [value];
    } else {
      schema[lastPath].push(value);
    }
  } else if (operation == "add") {
    // increment the existing (numeric) value
    if (!schema[lastPath]) {
      schema[lastPath] = 0;
    }
    if (!value) {
      value = 0;
    }
    schema[lastPath] += Number(value);
  } else {
    // simple set
    schema[lastPath] = value;
  }
}

// Deep-get nested object properties without throwing errors
export function get(obj, path) {
  let schema = obj;
  let paths = path.split(".");
  let len = paths.length;
  for (let i = 0; i < len - 1; i++) {
    let elem = paths[i];
    if (!schema[elem]) {
      return null;
    }
    schema = schema[elem];
  }
  return schema[paths[len - 1]];
}

// Sum the number of items in a given object member property
export function countMembers(obj, property) {
  let sum = 0;
  Object.keys(obj).forEach((key) => {
    let members = get(obj, `${key}.${property}`);
    if (members && members.length) {
      sum += members.length;
    }
  });
  return sum;
}

// Checks supplied properties exist as members of the object
// Returns an array that can be destructured, with arguments:
// [0] = valid (boolean), [1] = log message (string)
// Followed by all other arguments in the order supplied
export function validateRow(obj, props) {
  let response = [true, ""];
  for (let p of props) {
    if (obj[p] === "" || obj[p] === null || typeof obj[p] === "undefined") {
      response[0] = false;
      response[1] += `${p} = empty; `;
      obj[p] = "";
    } else {
      response[1] += `${p} = ${obj[p]}; `;
    }
    response.push(typeof obj[p] == 'string' ? obj[p].trim() : obj[p]);
  }
  if (response[0] === true) {
    // don't return a message for valid data
    response[1] = "";
  }
  return response;
}

// Format full name based on DB's internal format
export function formatPersonName(individual) {
  return `${individual.last_name ? individual.last_name.trim() : ""},${
    individual.middle_name ? " " + individual.middle_name.trim() : ""
  } ${individual.first_name ? individual.first_name.trim() : ""}`;
}

// Takes month by month data (Class Visits / Consent) and inserts missing months and year totals
// The data expected is in a specific format, refer to those pages to see implementation
export function generateCompleteMonthData(output, month, index, array, cols, rows, yearCounts) {
  const next = array[index + 1];
  let curYear = parseInt(month.substring(0, 4));
  let curMonth = parseInt(month.substring(5, 7));
  let nextYear = next ? parseInt(next.substring(0, 4)) : '';
  let nextMonth = next ? parseInt(next.substring(5, 7)) : '';
  let thisYear = new Date().getFullYear();
  let thisMonth = new Date().getMonth();
  output.push({
    title: month,
    data: cols.map(col => rows[month][col])
  });
  if (nextYear > curYear) {
    // insert remaining months from current row's year
    for (let m = curMonth + 1; m <= 12; m++) {
      output.push({
        title: `${curYear}-${m.toString().padStart(2, '0')}`,
        data: cols.map(() => ''),
      });
    }
  } else if (nextYear == curYear && nextMonth > curMonth + 1) {
    for (let m = curMonth + 1; m < nextMonth; m++) {
      output.push({
        title: `${curYear}-${m.toString().padStart(2, '0')}`,
        data: cols.map(() => ''),
      });
    }
  }
  if (!next) {
    if (curMonth < thisMonth || curYear < thisYear) {
      if (curYear < thisYear) { thisMonth = 12; }
      for (let m = curMonth; m <= thisMonth; m++) {
        output.push({
          title: `${curYear}-${m.toString().padStart(2, '0')}`,
          data: cols.map(() => ''),
        });
      }
    }
  }
  if ((next && curYear != nextYear) || !next) {
    output.push({
      title: `total for ${curYear}`,
      class: 'bg-secondary-light font-italic font-weight-normal',
      data: cols.map(col => yearCounts[curYear][col])
    });
  }
  if (nextYear > curYear) {
    // insert missing months at beginning of next year
    if (nextMonth > 1) {
      for (let m = 1; m < nextMonth; m++) {
        output.push({
          title: `${nextYear}-${m.toString().padStart(2, '0')}`,
          data: cols.map(() => ''),
        });
      }
    }
  }
  return output;
}

export function calculateHaversine ([lat1, lon1], [lat2, lon2]) {
  const toRadian = angle => (Math.PI / 180) * angle;
  const distance = (a, b) => (Math.PI / 180) * (a - b);
  const RADIUS_OF_EARTH_IN_KM = 6371;
  const dLat = distance(lat2, lat1);
  const dLon = distance(lon2, lon1);
  lat1 = toRadian(lat1);
  lat2 = toRadian(lat2);

  // Haversine Formula
  const a =
    Math.pow(Math.sin(dLat / 2), 2) +
    Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.asin(Math.sqrt(a));

  return RADIUS_OF_EARTH_IN_KM * c;
}

export function isDateMonthsAgo(date, months = 3) {
  let limit = new Date();
  limit.setMonth(limit.getMonth() - months);
  return date < limit ? true : false;
}

export function formatDate(date, isOnlyMonth=false) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}${isOnlyMonth ? '' : `-${String(date.getDate()).padStart(2, '0')}`}`;
}
